import { useCallback, useEffect, useRef, useState } from "react"
import {
  Animated,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { useFocusEffect, useLocalSearchParams } from "expo-router"
import { StatusBar } from "expo-status-bar"
import { Document, Page, pdfjs } from "react-pdf"

import "react-pdf/dist/Page/AnnotationLayer.css"
import "react-pdf/dist/Page/TextLayer.css"

import { SidekickView } from "src/components/SidekickView"
import { useMediaContext } from "src/context/MediaContextProvider"
import { useAppTheme } from "src/hooks/useAppTheme"
import { backToRoot } from "src/utils/routerHelpers"

if (pdfjs?.GlobalWorkerOptions) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`
}

export default function FileViewerScreen() {
  const {
    url: urlParam,
    title: titleParam,
    resourceId,
    taskId,
  } = useLocalSearchParams<{
    url?: string | string[]
    title?: string | string[]
    resourceId?: string
    taskId?: string
  }>()
  const resolvedUrl = Array.isArray(urlParam) ? urlParam[0] : urlParam
  const resolvedTitle = Array.isArray(titleParam) ? titleParam[0] : titleParam
  const { theme } = useAppTheme()
  const { colors } = theme
  const { width: windowWidth } = useWindowDimensions()

  const controlsOpacity = useRef(new Animated.Value(1)).current
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [scale, setScale] = useState(1)
  const [toolbarVisible, setToolbarVisible] = useState(true)
  const toolbarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { setMediaContext, clearMediaContext, updateOffset, updateTotalLength } = useMediaContext()

  // The pdfs returned from our backend don't end in .pdf so we assume they are

  useFocusEffect(
    useCallback(() => {
      // No orientation locking needed on web
    }, []),
  )

  useEffect(() => {
    if (resourceId) {
      setMediaContext({
        resourceId,
        taskId,
        title: resolvedTitle,
        initialOffset: "1",
      })
    }
    return () => {
      clearMediaContext()
    }
  }, [resourceId, taskId, resolvedTitle, setMediaContext, clearMediaContext])

  useEffect(() => {
    if (numPages > 0) {
      updateTotalLength(String(numPages))
    }
  }, [numPages, updateTotalLength])

  useEffect(() => {
    updateOffset(String(pageNumber))
  }, [pageNumber, updateOffset])

  const onDocumentLoadSuccess = useCallback(({ numPages: total }: { numPages: number }) => {
    setNumPages(total)
    setIsLoading(false)
    setHasError(false)
  }, [])

  const onDocumentLoadError = useCallback(() => {
    setIsLoading(false)
    setHasError(true)
  }, [])

  const goToPrevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(1, prev - 1))
  }, [])

  const goToNextPage = useCallback(() => {
    console.log("goToNextPage", numPages)
    setPageNumber((prev) => Math.min(numPages, prev + 1))
  }, [numPages])

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(3, prev + 0.25))
  }, [])

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(0.5, prev - 0.25))
  }, [])

  // Auto-hide toolbar after 3 seconds of no mouse activity
  const resetToolbarTimeout = useCallback(() => {
    setToolbarVisible(true)
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current)
    }
    toolbarTimeoutRef.current = setTimeout(() => {
      setToolbarVisible(false)
    }, 3000)
  }, [])

  useEffect(() => {
    if (!isLoading && numPages > 0) {
      resetToolbarTimeout()
    }
    return () => {
      if (toolbarTimeoutRef.current) {
        clearTimeout(toolbarTimeoutRef.current)
      }
    }
  }, [isLoading, numPages, resetToolbarTimeout])

  const handleToolbarMouseEnter = useCallback(() => {
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current)
    }
    setToolbarVisible(true)
  }, [])

  const handleToolbarMouseLeave = useCallback(() => {
    resetToolbarTimeout()
  }, [resetToolbarTimeout])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault()
        setPageNumber((prev) => Math.max(1, prev - 1))
      } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault()
        setPageNumber((prev) => Math.min(numPages, prev + 1))
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault()
        setScale((prev) => Math.min(3, prev + 0.25))
      } else if (e.key === "-") {
        e.preventDefault()
        setScale((prev) => Math.max(0.5, prev - 0.25))
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [numPages])

  if (!resolvedUrl) {
    return (
      <View style={styles.errorContainer}>
        <MaterialIcons name="error-outline" size={48} color="#FFFFFF" />
        <Text style={styles.errorText}>No file URL provided.</Text>
        <TouchableOpacity style={styles.backButton} onPress={() => backToRoot()}>
          <Text style={styles.backButtonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    )
  }

  // TODO: We may need a better way to detect if the file is a PDF or not
  // if (!isPdf) {
  //   return (
  //     <View style={styles.errorContainer}>
  //       <MaterialIcons name="picture-as-pdf" size={48} color="#FFFFFF" />
  //       <Text style={styles.errorTitle}>Unsupported File Type</Text>
  //       <Text style={styles.errorText}>Only PDF files are supported in the web viewer.</Text>
  //       <TouchableOpacity style={styles.backButton} onPress={() => backToRoot()}>
  //         <Text style={styles.backButtonText}>Go Back</Text>
  //       </TouchableOpacity>
  //     </View>
  //   )
  // }

  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      <View style={styles.closeButton}>
        <TouchableOpacity
          onPress={() => backToRoot()}
          style={[styles.closeButtonInner, { backgroundColor: colors.buttonBackground }]}
          accessibilityRole="button"
          accessibilityLabel="Close file viewer"
        >
          <MaterialIcons name="close" size={20} color={colors.buttonText} />
        </TouchableOpacity>
      </View>

      {/* PDF toolbar — floats over the document, above SidekickView */}
      {!hasError && numPages > 0 && (
        <div
          style={{
            ...webStyles.toolbar,
            opacity: toolbarVisible ? 1 : 0,
            pointerEvents: toolbarVisible ? ("auto" as const) : ("none" as const),
            transition: "opacity 0.3s ease",
          }}
          onMouseEnter={handleToolbarMouseEnter}
          onMouseLeave={handleToolbarMouseLeave}
        >
          <div style={webStyles.toolbarSection}>
            <button
              onClick={zoomOut}
              disabled={scale <= 0.5}
              style={webStyles.toolbarButton}
              title="Zoom out (-)"
            >
              <MaterialIcons name="remove" size={18} color="#FFFFFF" />
            </button>
            <span style={webStyles.zoomLabel}>{Math.round(scale * 100)}%</span>
            <button
              onClick={zoomIn}
              disabled={scale >= 3}
              style={webStyles.toolbarButton}
              title="Zoom in (+)"
            >
              <MaterialIcons name="add" size={18} color="#FFFFFF" />
            </button>
          </div>

          <div style={webStyles.toolbarSection}>
            <button
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              style={webStyles.toolbarButton}
              title="Previous page (←)"
            >
              <MaterialIcons name="chevron-left" size={22} color="#FFFFFF" />
            </button>
            <span style={webStyles.pageLabel}>
              {pageNumber} / {numPages}
            </span>
            <button
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              style={webStyles.toolbarButton}
              title="Next page (→)"
            >
              <MaterialIcons name="chevron-right" size={22} color="#FFFFFF" />
            </button>
          </div>
        </div>
      )}

      <SidekickView
        showAvatar
        autoShowAvatarOnSpeech
        showCaptions
        showCCToggle
        showButtonCluster
        compactButtonCluster
        darkBackground={false}
        showControlsBarBackground={false}
        controlsOpacity={controlsOpacity}
        controlsVisible={true}
        riveOwner="video"
      >
        <View style={styles.pdfOuterContainer}>
          {isLoading && (
            <View style={[styles.loadingContainer, { backgroundColor: colors.background }]}>
              <MaterialIcons name="hourglass-empty" size={32} color={colors.text} />
              <Text style={[styles.loadingText, { color: colors.text }]}>Loading PDF...</Text>
            </View>
          )}
          {hasError && (
            <View style={styles.errorOverlay}>
              <MaterialIcons name="error-outline" size={48} color="#FFFFFF" />
              <Text style={styles.errorTitle}>Failed to Load PDF</Text>
              <Text style={styles.errorText}>
                The PDF could not be loaded. Please check that the file URL is publicly accessible.
              </Text>
            </View>
          )}

          {/* Scrollable PDF area */}
          <div
            ref={scrollContainerRef}
            style={webStyles.scrollContainer}
            onMouseMove={resetToolbarTimeout}
          >
            <Document
              file={resolvedUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading=""
              error=""
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                width={Math.min(windowWidth - 80, 900)}
                renderAnnotationLayer
                renderTextLayer
              />
            </Document>
          </div>
        </View>
      </SidekickView>
    </View>
  )
}

const webStyles: Record<string, React.CSSProperties> = {
  scrollContainer: {
    flex: 1,
    overflow: "auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "16px 0",
  },
  toolbar: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 32,
    padding: "10px 16px",
    backgroundColor: "rgba(30, 30, 30, 0.9)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 11000,
  },
  toolbarSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  toolbarButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 8,
    border: "1px solid rgba(255, 255, 255, 0.2)",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    cursor: "pointer",
    padding: 0,
  },
  pageLabel: {
    color: "#FFFFFF",
    fontSize: 14,
    fontFamily: "system-ui, sans-serif",
    minWidth: 60,
    textAlign: "center" as const,
  },
  zoomLabel: {
    color: "#FFFFFF",
    fontSize: 13,
    fontFamily: "system-ui, sans-serif",
    minWidth: 44,
    textAlign: "center" as const,
  },
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#000000",
  },
  closeButton: {
    position: "fixed" as any,
    top: 22,
    right: 12,
    zIndex: 12000,
  },
  closeButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  pdfOuterContainer: {
    flex: 1,
    backgroundColor: "#1a1a1a",
  },
  errorContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "#000000",
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#FFFFFF",
    marginTop: 16,
    marginBottom: 8,
    textAlign: "center",
  },
  errorText: {
    fontSize: 14,
    color: "#FFFFFF",
    textAlign: "center",
    marginBottom: 8,
  },
  backButton: {
    backgroundColor: "#007AFF",
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  backButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#1a1a1a",
    zIndex: 1,
  },
  loadingText: {
    fontSize: 14,
    color: "#FFFFFF",
    marginTop: 12,
  },
  errorOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#1a1a1a",
    padding: 24,
    zIndex: 2,
  },
})
